import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Icon, ListGroup, ListRow } from '@uplink/ui';
import { useState } from 'react';
import { errorText, useTRPC } from './api.ts';
import { usePrefs } from './prefs.tsx';
import { haptic } from './telegram.ts';

/**
 * "Site on a server": points the domain and its www at one of the user's running servers in one tap.
 * Hidden when there is no server with an address yet.
 */
export function PointToServer({
  id,
  records,
  onDone,
}: {
  id: string;
  /** Current DNS records, to show which server the domain already points at */
  records: { host: string; type: string; value: string }[];
  onDone: () => void;
}) {
  const trpc = useTRPC();
  const { t } = usePrefs();
  const servers = useQuery(trpc.servers.list.queryOptions());
  const [done, setDone] = useState<string | null>(null);
  const point = useMutation(
    trpc.domains.pointToServer.mutationOptions({
      onSuccess: (_r, input) => {
        haptic.success();
        setDone(servers.data?.find((s) => s.id === input.serverId)?.name ?? '');
        onDone();
      },
      onError: () => haptic.error(),
    }),
  );

  const running = (servers.data ?? []).filter((s) => s.status === 'active' && s.ipv4);
  if (running.length === 0) return null;
  const rootIp = records.find((r) => r.host === '@' && r.type === 'A')?.value;

  return (
    <ListGroup
      header={t.domain.point}
      footer={done !== null ? t.domain.pointDone(done) : t.domain.pointFooter}
    >
      {running.map((s) => (
        <ListRow
          key={s.id}
          icon="server"
          title={s.name}
          subtitle={s.ipv4}
          trailing={
            rootIp === s.ipv4 ? (
              <span className="ul-pointed">
                <Icon name="check" size={16} />
                {t.domain.pointed}
              </span>
            ) : (
              <Button
                variant="secondary"
                loading={point.isPending && point.variables?.serverId === s.id}
                onClick={() => point.mutate({ id, serverId: s.id })}
              >
                {t.domain.pointTo}
              </Button>
            )
          }
        />
      ))}
      {point.isError ? (
        <div className="ul-error" role="alert">
          {errorText(point.error)}
        </div>
      ) : null}
    </ListGroup>
  );
}
